// INVIMP1 (docs/build-briefs/INVIMP1-inventory-import.md, INVIMP1-F1). Reading ONE cell of an import file into a typed
// value or a reason. Browser-safe: it imports nothing from Node or a database, because the server (preview and apply) and
// the Import inventory screen (the read-as line under a mapped column) both run these exact functions, so the screen
// cannot say a cell will be read one way while the server reads it another.

export type PriceParseFailure = "blank" | "not_a_number" | "not_positive" | "too_many_decimals";
export type PriceParse = { ok: true; cents: number } | { ok: false; reason: PriceParseFailure };

const DIGITS_WITH_THOUSANDS = /^(?:\d{1,3}(?:,\d{3})+|\d+)?(?:\.(\d*))?$/;

/**
 * "$28.70", "28.7", "1,028.70", " $203.70 " parse to exact integer cents. Blank,
 * "N/A", zero, negative, an exponent, more than two decimals (unless the extra
 * digits are zeros) and anything else are refused with a reason. A numeric
 * spreadsheet cell goes through its shortest decimal text first, never through
 * a multiply: 28.7 * 100 is 2869.9999999999995 in floating point.
 */
export function parsePriceCents(value: unknown): PriceParse {
  if (value === null || value === undefined) {
    return { ok: false, reason: "blank" };
  }
  let text: string;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return { ok: false, reason: "not_a_number" };
    }
    text = String(value);
  } else if (typeof value === "string") {
    text = value;
  } else {
    return { ok: false, reason: "not_a_number" };
  }
  text = text.trim();
  if (text === "") {
    return { ok: false, reason: "blank" };
  }
  let negative = false;
  if (text.startsWith("-")) {
    negative = true;
    text = text.slice(1).trim();
  }
  if (text.startsWith("$")) {
    text = text.slice(1).trim();
  }
  if (text.startsWith("-")) {
    negative = true;
    text = text.slice(1).trim();
  }
  const match = DIGITS_WITH_THOUSANDS.exec(text);
  if (match === null || !/\d/.test(text)) {
    return { ok: false, reason: "not_a_number" };
  }
  const fraction = match[1] ?? "";
  const whole = (text.split(".")[0] ?? "").replace(/,/g, "");
  if (whole.length > 12) {
    return { ok: false, reason: "not_a_number" };
  }
  if (fraction.length > 2 && /[1-9]/.test(fraction.slice(2))) {
    return { ok: false, reason: "too_many_decimals" };
  }
  const cents =
    BigInt(whole === "" ? "0" : whole) * 100n + BigInt(fraction.slice(0, 2).padEnd(2, "0"));
  if (negative || cents === 0n) {
    return { ok: false, reason: "not_positive" };
  }
  return { ok: true, cents: Number(cents) };
}

export type Parsed<T> = { ok: true; value: T } | { ok: false; reason: string };

/** A decimal with no sign, no exponent and at most `maxDecimals` places; commas as thousands separators allowed. */
function parseDecimalText(raw: string, maxDecimals: number): string | null {
  const text = raw.trim().replace(/^\+/, "");
  if (!/^(?:\d{1,3}(?:,\d{3})+|\d+)?(?:\.\d+)?$/.test(text) || !/\d/.test(text)) {
    return null;
  }
  const plain = text.replace(/,/g, "");
  const [whole = "", fraction = ""] = plain.split(".");
  if (fraction.length > maxDecimals && /[1-9]/.test(fraction.slice(maxDecimals))) {
    return null;
  }
  const kept = fraction.slice(0, maxDecimals).replace(/0+$/, "");
  const wholeText = whole === "" ? "0" : whole.replace(/^0+(?=\d)/, "");
  return kept === "" ? wholeText : `${wholeText}.${kept}`;
}

/** Par level: a decimal of at least zero. "1.5" and "2" are fine, "abc" and "-1" are not. */
export function parseParLevel(raw: string): Parsed<number> {
  const decimal = parseDecimalText(raw, 4);
  if (decimal === null) {
    return { ok: false, reason: "Par is not a number" };
  }
  return { ok: true, value: Number(decimal) };
}

/** A valid number below zero ("-2.0252"). Other systems export negative counts; stock is read as 0 with a note, not refused. */
export function isNegativeStock(raw: string): boolean {
  const text = raw.trim();
  if (!text.startsWith("-")) return false;
  const decimal = parseDecimalText(text.slice(1).trim(), 4);
  return decimal !== null && Number(decimal) > 0;
}

export const NEGATIVE_STOCK_NOTE = "Negative count set to 0";

/** Starting stock: a decimal string of at least zero with at most four places, as the opening balance wants it. */
export function parseStockQuantity(raw: string): Parsed<string> {
  if (isNegativeStock(raw)) {
    return { ok: true, value: "0" };
  }
  const decimal = parseDecimalText(raw, 4);
  if (decimal === null) {
    return { ok: false, reason: "Starting stock is not a number" };
  }
  return { ok: true, value: decimal };
}

/** A price in dollars to integer cents, through the vendor price list's own parser (never a float multiply). */
export function parseMoney(raw: string, label: string): Parsed<number> {
  const parsed = parsePriceCents(raw);
  if (parsed.ok) {
    return { ok: true, value: parsed.cents };
  }
  switch (parsed.reason) {
    case "too_many_decimals":
      return { ok: false, reason: `${label} has more than two decimals` };
    case "not_positive":
      return { ok: false, reason: `${label} must be more than zero` };
    default:
      return { ok: false, reason: `${label} is not a price` };
  }
}

/** "40", "40%", "40.5 %" to basis points (4000, 4050). 0 to 100 only. */
export function parseAbvBasisPoints(raw: string): Parsed<number> {
  const decimal = parseDecimalText(raw.trim().replace(/%$/, "").trim(), 2);
  if (decimal === null || Number(decimal) > 100) {
    return { ok: false, reason: "ABV is not a percentage" };
  }
  const [whole = "0", fraction = ""] = decimal.split(".");
  return { ok: true, value: Number(whole) * 100 + Number(fraction.padEnd(2, "0")) };
}

export interface ParsedSize {
  sizeValue: number;
  sizeUnit: "ml" | "l" | "oz";
}

const SIZE_PATTERN = /^(\d*\.?\d+)\s*([a-z.\s]+)$/;

/**
 * "750 ml", "1.75 L", "1L", "12 oz", "12 fl oz", "70 cl" to a value and one of the units the item editor stores
 * (ml, l, oz). A centilitre is converted to ml. Anything else, a gallon included, is refused rather than guessed.
 */
export function parseSize(raw: string): Parsed<ParsedSize> {
  const match = SIZE_PATTERN.exec(raw.trim().toLowerCase());
  if (match === null) {
    return { ok: false, reason: "Size is not understood" };
  }
  const amount = Number(match[1]);
  const unit = (match[2] ?? "").replace(/[.\s]/g, "");
  if (!Number.isFinite(amount) || amount <= 0) {
    return { ok: false, reason: "Size is not understood" };
  }
  switch (unit) {
    case "ml":
    case "milliliter":
    case "milliliters":
    case "millilitre":
    case "millilitres":
      return { ok: true, value: { sizeValue: amount, sizeUnit: "ml" } };
    case "cl":
      return { ok: true, value: { sizeValue: amount * 10, sizeUnit: "ml" } };
    case "l":
    case "lt":
    case "ltr":
    case "liter":
    case "liters":
    case "litre":
    case "litres":
      return { ok: true, value: { sizeValue: amount, sizeUnit: "l" } };
    case "oz":
    case "floz":
    case "ounce":
    case "ounces":
      return { ok: true, value: { sizeValue: amount, sizeUnit: "oz" } };
    default:
      return { ok: false, reason: "Size is not understood" };
  }
}

/** A barcode as text: trims, drops a spreadsheet's trailing ".0" and text apostrophe, never reformats the digits. */
export function cleanBarcode(raw: string): string {
  let text = raw.trim();
  if (text.startsWith("'") || text.startsWith("’")) {
    text = text.slice(1).trim();
  }
  const trailingZero = /^(\d+)\.0+$/.exec(text);
  if (trailingZero !== null) {
    text = trailingZero[1] as string;
  }
  return text.replace(/\s+/g, "");
}

/**
 * Size from a Size cell and, when one is mapped, a Size unit cell. A Size cell that already holds a unit ("750 ml")
 * is read as it is and the unit column is not consulted. A bare number takes the unit column's unit through the same
 * parse ("750" and "ml" is 750 ml). A bare number with no unit is refused rather than guessed.
 */
export function parseSizeWithUnit(sizeCell: string, unitCell: string): Parsed<ParsedSize> {
  const size = sizeCell.trim();
  if (/^\d*\.?\d+$/.test(size)) {
    if (unitCell.trim() === "") {
      return { ok: false, reason: "Size needs a unit" };
    }
    const joined = parseSize(`${size} ${unitCell.trim()}`);
    return joined.ok ? joined : { ok: false, reason: "Size unit is not understood" };
  }
  return parseSize(size);
}

export function sizeText(size: ParsedSize): string {
  return `${size.sizeValue} ${size.sizeUnit === "l" ? "L" : size.sizeUnit}`;
}

/** The mapped fields whose cell has a read-as line, and what each one reads. */
export const READ_AS_FIELDS = [
  "size",
  "par",
  "cost",
  "sellPrice",
  "abv",
  "startingStock",
  "barcode",
] as const;
export type ReadAsFieldId = (typeof READ_AS_FIELDS)[number];

export function isReadAsField(id: string): id is ReadAsFieldId {
  return (READ_AS_FIELDS as readonly string[]).includes(id);
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * How the server will read one cell for one field, as short text, or the server's own reason when it would refuse
 * it. Runs the same parse functions the preview runs. `unitCell` is only used for Size.
 */
export function readCellAs(field: ReadAsFieldId, cell: string, unitCell = ""): Parsed<string> {
  const text = cell.trim().replace(/\s+/g, " ");
  switch (field) {
    case "size": {
      const parsed = parseSizeWithUnit(text, unitCell.trim().replace(/\s+/g, " "));
      return parsed.ok ? { ok: true, value: sizeText(parsed.value) } : parsed;
    }
    case "par": {
      const parsed = parseParLevel(text);
      return parsed.ok ? { ok: true, value: String(parsed.value) } : parsed;
    }
    case "cost":
    case "sellPrice": {
      const parsed = parseMoney(text, field === "cost" ? "Cost" : "Sell price");
      return parsed.ok ? { ok: true, value: dollars(parsed.value) } : parsed;
    }
    case "abv": {
      const parsed = parseAbvBasisPoints(text);
      return parsed.ok ? { ok: true, value: `${parsed.value / 100}%` } : parsed;
    }
    case "startingStock": {
      return parseStockQuantity(text);
    }
    case "barcode": {
      const code = cleanBarcode(text);
      return code === "" ? { ok: false, reason: "Barcode is blank" } : { ok: true, value: code };
    }
  }
}
